import React from 'react';
import PropTypes from 'prop-types';

import TimelineBase, {
  DateHeader,
  CustomMarker,
  SidebarHeader,
  TimelineMarkers,
  TimelineHeaders,
} from 'react-calendar-timeline/lib';

import {
  getCanvasWidth,
  stackTimelineItems,
} from 'react-calendar-timeline/lib/lib/utility/calendar';

import clsx from 'clsx';
import i18n from 'i18n-js';
import moment from 'moment';
import { buildUrl } from 'utils';
import { Tooltip } from 'components/UI';
import { Link } from 'react-router-dom';
import useStyles from './Calendar.styled';
import defaultClasses from './Calendar.classes';
import { Box, Typography } from '@mui/material';
import 'react-calendar-timeline/lib/Timeline.css';
import QueryBuilder from '@mui/icons-material/QueryBuilder';
import containerResizeDetector from 'react-calendar-timeline/lib/resize-detector/container';

class Timeline extends TimelineBase {
  // Override resize method checking intermediate container reference
  // https://github.com/namespace-ee/react-calendar-timeline/blob/master/src/lib/Timeline.js
  resize = (props = this.props) => {
    const containerWidth = this.container?.getBoundingClientRect()?.width;
    if (containerWidth) {
      let width = containerWidth - props.sidebarWidth - props.rightSidebarWidth;
      const canvasWidth = getCanvasWidth(width, props.buffer);
      const { dimensionItems, height, groupHeights, groupTops } = stackTimelineItems(
        props.items,
        props.groups,
        canvasWidth,
        this.state.canvasTimeStart,
        this.state.canvasTimeEnd,
        props.keys,
        props.lineHeight,
        props.itemHeightRatio,
        props.stackItems,
        this.state.draggingItem,
        this.state.resizingItem,
        this.state.dragTime,
        this.state.resizingEdge,
        this.state.resizeTime,
        this.state.newGroupOrder
      );

      this.setState({
        width,
        dimensionItems,
        height,
        groupHeights,
        groupTops,
      });

      const scrollLeft = width * ((props.buffer - 1) / 2);
      this.scrollComponent.scrollLeft = scrollLeft;
      this.scrollHeaderRef.scrollLeft = scrollLeft;
    }
  };
}

// default zoom steps (zero to avoid zoom step)
const timeSteps = {
  second: 0,
  minute: 0,
  hour: 0,
  day: 1,
  month: 1,
  year: 1,
};

// default format object
const defaultFormat = {
  year: {
    long: 'YYYY',
    mediumLong: 'YYYY',
    medium: 'YYYY',
    short: 'YY',
  },
  month: {
    long: 'MMMM YYYY',
    mediumLong: 'MMMM, YYYY',
    medium: 'MMMM, YYYY',
    short: 'MM/YY',
  },
  week: {
    long: 'w',
    mediumLong: 'w',
    medium: 'w',
    short: 'w',
  },
  day: {
    long: 'ddd MMM D, YYYY',
    mediumLong: 'ddd MMM D, YYYY',
    medium: 'MMM D, YYYY',
    short: 'MM/DD/YY',
  },
  hour: {
    long: 'HH:00',
    mediumLong: 'HH:00',
    medium: 'HH:00',
    short: 'HH',
  },
  minute: {
    long: 'HH:mm',
    mediumLong: 'HH:mm',
    medium: 'HH:mm',
    short: 'mm',
  },
};

const keys = {
  itemIdKey: 'key',
  groupIdKey: 'key',
  groupTitleKey: 'name',
  itemGroupKey: 'groupId',
  itemTimeEndKey: 'endUsed',
  itemTitleKey: 'information',
  itemTimeStartKey: 'startUsed',
};

const CalendarTimeline = ({
  tz,
  org,
  items,
  groups,
  endDate,
  settings,
  startDate,
  onTimeChange,
  filteredItem,
  visibleTimeEnd,
  onBoundsChange,
  visibleTimeStart,
  onItemDoubleClick,
  onTimelineClickHandler,
}) => {
  const classes = useStyles();
  /**
   * Render item
   */
  const itemRenderer = ({ item, itemContext, getItemProps, getResizeProps }) => {
    const { left: leftResizeProps, right: rightResizeProps } = getResizeProps();
    const name = item.id ? `${item.name} - ` : item?.name || '';
    return (
      <div
        data-cy={'calendar-timeline-item'}
        {...getItemProps({
          style: {
            color: '#FFF',
            borderStyle: 'solid',
            borderWidth: 1,
            borderRadius: 4,
            borderLeftWidth: itemContext.selected ? 3 : 1,
            borderRightWidth: itemContext.selected ? 3 : 1,
            ...item.styles,
          },
        })}
      >
        {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : null}

        <div
          style={{
            overflow: 'hidden',
            paddingLeft: 3,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.status === 'OFFLINE' && item.offlineReason
            ? `${i18n.t(`reasons.${item.offlineReason}`) || 'UNKNOWN'} ${
                item.description ? ' - ' + item.description : ''
              }`
            : item?.type !== 'BUFFER'
              ? `${name}${item.id}`
              : ''}
        </div>

        {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : null}
      </div>
    );
  };

  /**
   * Handle label format
   * @param {*} time
   * @param {*} unit
   * @param {*} labelWidth
   * @param {*} formatOptions
   */
  const labelFormatter = ([timeStart], unit, labelWidth, formatOptions = defaultFormat) => {
    let format;
    if (labelWidth >= 150) {
      format = formatOptions[unit]['long'];
    } else if (labelWidth >= 100) {
      format = formatOptions[unit]['mediumLong'];
    } else if (labelWidth >= 50) {
      format = formatOptions[unit]['medium'];
    } else {
      format = formatOptions[unit]['short'];
    }
    return timeStart.format(format);
  };

  /**
   * Format dates
   * @param {object} settings
   * @param {string} type
   */
  const formatDate = (date, settings, type) => {
    const dateSettings = {
      time: settings[type].time,
      count: settings[type].count,
    };

    return new Date(
      moment(date).add(dateSettings.count, dateSettings.time).format('YYYY/MM/DD HH:mm:ss')
    );
  };

  const EmptyGroup = () => (
    <Box display="flex" maxHeight={65} height="100%">
      <Box
        minWidth={200}
        px={1.5}
        maxWidth={200}
        className={clsx(classes.sidebarColumn, classes.vgColumn)}
      />
      <Box width={250} px={1.5} className={clsx(classes.sidebarColumn, classes.vehicle)} />
    </Box>
  );

  /**
   * Fill Groups
   */
  const fillGroups = (groups = []) => {
    if (!groups?.length) {
      groups.push({ id: 'empty-group', name: <EmptyGroup /> });
      return groups;
    }
    const parsedGroups = groups.map((item) => ({
      ...item,
      selected: item.id === filteredItem,
    }));
    parsedGroups.forEach((group) => {
      if (group.name && typeof group.name === 'string') {
        group.name = (
          <Box display="flex" maxHeight={65} height="100%">
            <Box
              minWidth={200}
              px={1.5}
              maxWidth={200}
              sx={{ ...defaultClasses.sidebarColumn, ...defaultClasses.vgColumn }}
            >
              <Tooltip arrow placement="top" title={group?.vehicleGroup?.name}>
                <Box display="flex" flexDirection="column" width="100%" maxWidth={180}>
                  <Typography sx={defaultClasses.vehicleGroup}>
                    {group?.vehicleGroup?.name || '--'}
                  </Typography>
                  {group?.vehicleGroup?.parent?.name && (
                    <>
                      <Typography sx={defaultClasses.location}>
                        {group?.location?.name || '--'}
                      </Typography>
                      {org === 'msServiceOrg' && (
                        <Typography sx={defaultClasses.serviceArea}>
                          {group?.vehicleGroup?.parent?.name || '--'}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Tooltip>
            </Box>
            <Box
              px={1.5}
              width={250}
              sx={{ ...defaultClasses.sidebarColumn, ...defaultClasses.vehicle }}
            >
              <Box display="flex" alignItems="center" sx={defaultClasses.vehicleTitle}>
                <Link to={buildUrl(`/vehicles/${group.id}`)}>
                  <Typography sx={defaultClasses.groupLink}>{group.name}</Typography>
                </Link>
                {group?.vehicleGroup && group?.vehicleGroup?.time_zone && (
                  <Box display="flex" alignItems="center">
                    <Box mx={0.25} lineHeight="0.625rem">
                      -
                    </Box>
                    <Box
                      fontSize={11}
                      display="flex"
                      alignItems="center"
                      sx={{
                        ...defaultClasses.timezone,
                        ...(group?.vehicleGroup?.time_zone !== tz &&
                          defaultClasses.differentTimezone),
                      }}
                    >
                      {moment().tz(group?.vehicleGroup?.time_zone).format('UTC Z z')}
                    </Box>
                    {group?.vehicleGroup?.time_zone !== tz && (
                      <Box>
                        <QueryBuilder sx={defaultClasses.timezoneIcon} />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <Box fontSize={13} display="flex" alignItems="center">
                <Typography sx={defaultClasses.vgInfo}>
                  {group?.vehicleType?.year} - {group?.vehicleType?.make} -{' '}
                  {group?.vehicleType?.model}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      }
    });

    return parsedGroups;
  };

  /**
   * Render group
   * @param {object} group
   */
  const groupRenderer = ({ group }) => {
    const className = group.selected ? defaultClasses.selectedGroup : defaultClasses.group;
    return <Box sx={className}>{group.name}</Box>;
  };

  return (
    <Timeline
      keys={keys}
      items={items}
      lineHeight={65}
      key={`${tz}`} // Used to rerender on prop changes
      sidebarWidth={450}
      timeSteps={timeSteps}
      minZoom={settings.minZoom}
      maxZoom={settings.maxZoom}
      itemTouchSendsClick={true}
      groups={fillGroups(groups)}
      onTimeChange={onTimeChange}
      itemRenderer={itemRenderer}
      groupRenderer={groupRenderer}
      onBoundsChange={onBoundsChange}
      visibleTimeStart={visibleTimeStart}
      visibleTimeEnd={visibleTimeEnd}
      onItemClick={onTimelineClickHandler}
      onItemDoubleClick={onItemDoubleClick}
      onItemSelect={onTimelineClickHandler}
      resizeDetector={containerResizeDetector}
      traditionalZoom={settings.traditionalZoom}
      defaultTimeEnd={formatDate(endDate, settings, 'endDate')}
      defaultTimeStart={formatDate(startDate, settings, 'startDate')}
      className={clsx(classes.calendarContainer, 'calendar-timeline-container')}
    >
      <TimelineHeaders className="sticky">
        <SidebarHeader>
          {({ getRootProps }) => (
            <Box sx={{ ...defaultClasses.calendarSidebar }} {...getRootProps()}>
              <Box
                width={200}
                sx={{ ...defaultClasses.sidebarColumn, ...defaultClasses.sidebarFirstColumn }}
              >
                {
                  org === 'msOwnerOrg'
                    ? i18n.t('dashboard.service_area')
                    : org === 'msServiceOrg'
                      ? i18n.t('dashboard.vehicle_group_location_service_area')
                      : i18n.t('dashboard.vehicle_group_location') // plain orgs
                }
              </Box>
              <Box width={250} sx={defaultClasses.sidebarColumn}>
                {i18n.t('dashboard.vehicle_timezone')}
              </Box>
            </Box>
          )}
        </SidebarHeader>
        <DateHeader labelFormat={labelFormatter} />
      </TimelineHeaders>
      <TimelineMarkers>
        <CustomMarker
          date={new Date(moment(new Date()).tz(tz).format('YYYY/MM/DD HH:mm:ss')).getTime()}
        >
          {({ styles }) => {
            styles = {
              ...styles,
              backgroundColor: settings.todayMarker.backgroundColor,
            };
            return <Box sx={{ ...styles }} />;
          }}
        </CustomMarker>
      </TimelineMarkers>
    </Timeline>
  );
};

CalendarTimeline.propTypes = {
  tz: PropTypes.any,
  org: PropTypes.string,
  groups: PropTypes.array.isRequired,
  items: PropTypes.array.isRequired,
  onItemDoubleClick: PropTypes.func,
  onTimelineClickHandler: PropTypes.func,
  onTimeChange: PropTypes.func,
  filteredItem: PropTypes.any,
  visibleTimeEnd: PropTypes.any,
  onBoundsChange: PropTypes.func,
  visibleTimeStart: PropTypes.any,
  endDate: PropTypes.any.isRequired,
  startDate: PropTypes.any.isRequired,
  settings: PropTypes.object.isRequired,
};

CalendarTimeline.defaultProps = {};

export { CalendarTimeline };
